import { create } from 'zustand';

interface DialogState {
  isOpen: boolean;
  title: string;
  content: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  onClose?: () => void;
  showCloseButton?: boolean;
  className?: string;
}

interface ToastState {
  isOpen: boolean;
  message: string;
  type: 'success' | 'error' | 'warning' | 'info';
  duration?: number;
  position?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
}

interface ModalState {
  isOpen: boolean;
  content: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  onClose?: () => void;
  showCloseButton?: boolean;
  className?: string;
}

interface UIState {
  dialogs: Record<string, DialogState>;
  toasts: ToastState[];
  modals: Record<string, ModalState>;
  isPending: boolean;
  // Dialog methods
  openDialog: (id: string, dialog: Omit<DialogState, 'isOpen'>) => void;
  closeDialog: (id: string) => void;
  updateDialog: (id: string, updates: Partial<DialogState>) => void;
  // Toast methods
  showToast: (message: string, type: ToastState['type'], options?: Omit<ToastState, 'isOpen' | 'message' | 'type'>) => void;
  removeToast: (index: number) => void;
  // Modal methods
  openModal: (id: string, modal: Omit<ModalState, 'isOpen'>) => void;
  closeModal: (id: string) => void;
  updateModal: (id: string, updates: Partial<ModalState>) => void;
  // Loading state
  setIsPending: (loading: boolean) => void;
  // Utility methods
  closeAllDialogs: () => void;
  closeAllModals: () => void;
  clearAllToasts: () => void;
}

const initialState: Omit<UIState, 
  'openDialog' | 'closeDialog' | 'updateDialog' |
  'showToast' | 'removeToast' |
  'openModal' | 'closeModal' | 'updateModal' |
  'setIsPending' | 'closeAllDialogs' | 'closeAllModals' | 'clearAllToasts'
> = {
  dialogs: {},
  toasts: [],
  modals: {},
  isPending: false,
};

export const useUIStore = create<UIState>()((set) => ({
  ...initialState,
  // Dialog methods
  openDialog: (id, dialog) =>
    set((state) => ({
      dialogs: {
        ...state.dialogs,
        [id]: {
          ...dialog,
          isOpen: true,
        },
      },
    })),
  closeDialog: (id) =>
    set((state) => {
      const { [id]: dialog, ...rest } = state.dialogs;
      if (dialog?.onClose) {
        dialog.onClose();
      }
      return { dialogs: rest };
    }),
  updateDialog: (id, updates) =>
    set((state) => ({
      dialogs: {
        ...state.dialogs,
        [id]: {
          ...state.dialogs[id],
          ...updates,
        },
      },
    })),
  // Toast methods
  showToast: (message, type, options = {}) =>
    set((state) => ({
      toasts: [
        ...state.toasts,
        {
          isOpen: true,
          message,
          type,
          duration: options.duration || 5000,
          position: options.position || 'bottom-right',
        },
      ],
    })),
  removeToast: (index) =>
    set((state) => ({
      toasts: state.toasts.filter((_, i) => i !== index),
    })),
  // Modal methods
  openModal: (id, modal) =>
    set((state) => ({
      modals: {
        ...state.modals,
        [id]: {
          ...modal,
          isOpen: true,
        },
      },
    })),
  closeModal: (id) =>
    set((state) => {
      const { [id]: modal, ...rest } = state.modals;
      if (modal?.onClose) {
        modal.onClose();
      }
      return { modals: rest };
    }),
  updateModal: (id, updates) =>
    set((state) => ({
      modals: {
        ...state.modals,
        [id]: {
          ...state.modals[id],
          ...updates,
        },
      },
    })),
  // Loading state
  setIsPending: (loading) =>
    set(() => ({
      isPending: loading,
    })),
  // Utility methods
  closeAllDialogs: () =>
    set((state) => {
      Object.values(state.dialogs).forEach((dialog) => {
        if (dialog.onClose) {
          dialog.onClose();
        }
      });
      return { dialogs: {} };
    }),
  closeAllModals: () =>
    set((state) => {
      Object.values(state.modals).forEach((modal) => {
        if (modal.onClose) {
          modal.onClose();
        }
      });
      return { modals: {} };
    }),
  clearAllToasts: () =>
    set(() => ({
      toasts: [],
    })),
})); 